import { Link } from "react-router-dom";
import type { Topic } from "@/mocks/topics";
import { getCategoryById } from "@/mocks/topics";

interface TopicCardProps {
  topic: Topic;
  variant?: "default" | "horizontal" | "compact";
}

export default function TopicCard({ topic, variant = "default" }: TopicCardProps) {
  const category = getCategoryById(topic.categoryId);

  if (variant === "horizontal") {
    return (
      <article className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row md:p-5">
        <div className="h-44 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-32 sm:w-52">
          <img
            src={topic.cover}
            alt={`${topic.title} 事件复盘配图`}
            title={`${topic.title} - 热点复盘`}
            className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            {category && (
              <span className="inline-flex items-center gap-1 rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                <i className={category.icon}></i>
                {category.name}
              </span>
            )}
            <span className="text-xs text-foreground-500">{topic.publishedAt}</span>
          </div>
          <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950">
            <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-600">
              {topic.title}
            </Link>
          </h3>
          <p className="mt-2 line-clamp-2 text-sm text-foreground-600">{topic.summary}</p>
          <div className="mt-auto flex items-center gap-4 pt-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {topic.readMinutes} 分钟
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-file-list-3-line"></i>
              {topic.timeline.length} 个节点
            </span>
            <span className="flex items-center gap-1 text-primary-600">
              <i className="ri-fire-line"></i>
              热度 {topic.heat}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === "compact") {
    return (
      <article className="group flex items-start gap-3">
        <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={topic.cover}
            alt={`${topic.title} 事件复盘缩略图`}
            title={`${topic.title} - 热点复盘`}
            className="h-full w-full object-top object-cover"
          />
        </div>
        <div className="min-w-0">
          <h4 className="font-heading text-sm font-semibold leading-snug text-foreground-950">
            <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-600">
              {topic.title}
            </Link>
          </h4>
          <p className="mt-1 text-xs text-foreground-500">
            {topic.publishedAt} · 热度 {topic.heat}
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <div className="h-44 w-full overflow-hidden bg-background-200 md:h-48">
        <img
          src={topic.cover}
          alt={`${topic.title} 事件复盘配图`}
          title={`${topic.title} - 热点复盘`}
          className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2">
          {category && (
            <span className="inline-flex items-center gap-1 rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
              <i className={category.icon}></i>
              {category.name}
            </span>
          )}
          <span className="text-xs text-foreground-500">{topic.publishedAt}</span>
        </div>
        <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
          <Link to={`/topic/${topic.id}`} className="transition-colors hover:text-primary-600">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {topic.readMinutes} 分钟读完
          </span>
          <span className="flex items-center gap-1 text-primary-600">
            <i className="ri-fire-line"></i>
            热度 {topic.heat}
          </span>
        </div>
      </div>
    </article>
  );
}